/**
 * LivePortalMap — a compact SVG equirectangular world map that lights
 * up markers for places with live SocialPulse portals. Called from the
 * /places hub. No external map library — the world contours are drawn
 * as simplified continent silhouettes and place markers use plain
 * lat/lng → equirectangular projection.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Activity, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

// A tiny hand-picked slug catalogue with lat/lng — the map will only
// render markers for slugs that also have a live doc in `/api/locations`.
// This keeps the map curated and prevents 1000s of dots at low zoom.
const CANDIDATES = [
  { slug: "india/kerala",                       lat: 10.85, lng: 76.27,  tint: "#059669" },
  { slug: "india/kerala/kottayam/kadanad",      lat:  9.75, lng: 76.72,  tint: "#0d9488" },
  { slug: "india/tamil-nadu/chennai",           lat: 13.08, lng: 80.27,  tint: "#ea580c" },
  { slug: "india/karnataka/bengaluru",          lat: 12.97, lng: 77.59,  tint: "#e11d48" },
  { slug: "india/maharashtra/mumbai",           lat: 19.07, lng: 72.87,  tint: "#f59e0b" },
  { slug: "india/delhi",                        lat: 28.61, lng: 77.20,  tint: "#7c3aed" },
  { slug: "india/west-bengal/kolkata",          lat: 22.57, lng: 88.36,  tint: "#0891b2" },
  { slug: "united-states/new-york",             lat: 40.71, lng: -74.00, tint: "#2563eb" },
  { slug: "united-states/california/san-jose",  lat: 37.34, lng: -121.89,tint: "#7c3aed" },
  { slug: "united-states/california/san-francisco", lat: 37.77, lng: -122.42, tint: "#0ea5e9" },
  { slug: "united-states/california/los-angeles",   lat: 34.05, lng: -118.24, tint: "#f59e0b" },
  { slug: "united-kingdom/england/london",      lat: 51.51, lng: -0.13,  tint: "#4f46e5" },
  { slug: "france/ile-de-france/paris",         lat: 48.86, lng: 2.35,   tint: "#db2777" },
  { slug: "united-arab-emirates/dubai",         lat: 25.20, lng: 55.27,  tint: "#f59e0b" },
  { slug: "singapore",                          lat: 1.35,  lng: 103.82, tint: "#059669" },
  { slug: "australia/new-south-wales/sydney",   lat: -33.87,lng: 151.21, tint: "#0891b2" },
];

const W = 720;
const H = 340;

function project(lat, lng) {
  // Equirectangular projection into a 720×340 viewport.
  return {
    x: ((lng + 180) / 360) * W,
    y: ((90 - lat) / 180) * H,
  };
}

export default function LivePortalMap() {
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(CANDIDATES.map(async (c) => {
          try {
            const r = await axios.get(`${API}/locations/${c.slug}`, { timeout: 4000 });
            return { ...c, name: r.data?.name || c.slug.split("/").pop(), href: `/l/${c.slug}` };
          } catch { return null; }
        }));
        if (!cancelled) setLive(results.filter(Boolean));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="relative rounded-3xl ring-1 ring-slate-200 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #F0FDF4 0%, #EEF2FF 50%, #FEF3C7 100%)" }}
      data-testid="sp-places-livemap"
    >
      <div className="flex flex-wrap items-center gap-2 px-5 sm:px-6 pt-5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur ring-1 ring-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">
          <Activity className="w-3 h-3" strokeWidth={2.5} /> Live portal map
        </span>
        <p className="text-sm text-slate-600">
          {loading ? (
            <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning the network…</span>
          ) : (
            <><span className="font-semibold text-slate-800">{live.length}</span> place{live.length === 1 ? "" : "s"} lit up right now.</>
          )}
        </p>
      </div>

      <div className="p-4 sm:p-6 overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto min-w-[640px]"
          role="img"
          aria-label="World map of live SocialPulse portals"
        >
          {/* Soft grid background */}
          <defs>
            <pattern id="sp-map-grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#CBD5E1" strokeWidth="0.35" opacity="0.35" />
            </pattern>
            <radialGradient id="sp-map-pulse" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="currentColor" stopOpacity="0.6" />
              <stop offset="80%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width={W} height={H} fill="url(#sp-map-grid)" />

          {/* Simplified continent silhouettes — hand-authored paths in
              equirectangular space. Not geographically precise but reads
              instantly as a world map. */}
          <g fill="#E2E8F0" stroke="#94A3B8" strokeWidth="0.5" opacity="0.85">
            {/* North America */}
            <path d="M 90 60 Q 110 55 140 60 L 175 65 L 185 90 L 175 115 L 160 130 L 145 145 L 135 155 L 120 155 L 110 140 L 100 120 L 90 100 Z" />
            {/* Central + South America */}
            <path d="M 175 155 L 195 160 L 220 175 L 235 210 L 225 250 L 210 280 L 200 300 L 190 295 L 180 260 L 175 220 L 172 190 Z" />
            {/* Europe */}
            <path d="M 340 65 L 380 60 L 405 75 L 400 100 L 385 110 L 365 105 L 345 100 L 335 85 Z" />
            {/* Africa */}
            <path d="M 355 115 L 395 115 L 415 140 L 420 175 L 415 210 L 400 245 L 385 260 L 370 255 L 360 220 L 355 180 L 350 145 Z" />
            {/* Asia */}
            <path d="M 405 55 L 460 55 L 515 60 L 565 75 L 590 90 L 595 115 L 585 140 L 555 145 L 520 140 L 490 130 L 460 125 L 435 115 L 415 100 L 405 85 Z" />
            {/* Middle East / India */}
            <path d="M 440 130 L 480 135 L 505 145 L 520 165 L 510 180 L 490 185 L 470 175 L 455 160 L 445 145 Z" />
            {/* SE Asia + Indonesia */}
            <path d="M 545 165 L 585 170 L 610 175 L 625 190 L 615 205 L 590 210 L 555 205 L 540 195 Z" />
            {/* Australia */}
            <path d="M 600 240 L 660 235 L 685 245 L 690 265 L 665 280 L 620 280 L 600 265 Z" />
          </g>

          {/* Static faint dots for candidate places (not yet live) */}
          {CANDIDATES.map((c) => {
            const p = project(c.lat, c.lng);
            return (
              <circle
                key={`bg-${c.slug}`}
                cx={p.x}
                cy={p.y}
                r={2}
                fill="#94A3B8"
                opacity="0.4"
              />
            );
          })}

          {/* Live pulses */}
          {live.map((c) => {
            const p = project(c.lat, c.lng);
            return (
              <g key={c.slug} style={{ color: c.tint }}>
                {/* Outer pulse (animated with a CSS class below) */}
                <circle cx={p.x} cy={p.y} r={14} fill="url(#sp-map-pulse)" className="sp-map-pulse-outer">
                  <animate attributeName="r" values="8;20;8" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;0;0.7" dur="3s" repeatCount="indefinite" />
                </circle>
                {/* Core */}
                <circle cx={p.x} cy={p.y} r={4.5} fill={c.tint} stroke="white" strokeWidth="1.5" />
                <Link to={c.href}>
                  <circle cx={p.x} cy={p.y} r={12} fill="transparent" className="cursor-pointer" data-testid={`sp-map-dot-${c.slug.replace(/[^a-z0-9]+/g,'-')}`}>
                    <title>{c.name}</title>
                  </circle>
                </Link>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend / live list */}
      <div className="px-5 sm:px-6 pb-5">
        <p className="text-[11px] font-semibold uppercase tracking-[.15em] text-slate-500 mb-2">Live portals</p>
        <ul className="flex flex-wrap gap-2" data-testid="sp-map-legend">
          {live.map((c) => (
            <li key={c.slug}>
              <Link
                to={c.href}
                data-testid={`sp-map-legend-${c.slug.replace(/[^a-z0-9]+/g,'-')}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:ring-violet-300 hover:text-violet-700 transition-colors"
              >
                <span className="w-2 h-2 rounded-full" style={{ background: c.tint }} />
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
